import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import {
	forwardRef,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	TouchEventHandler,
	useCallback,
	useState,
	FocusEvent,
	createContext,
	useContext,
	useMemo,
	KeyboardEvent
} from "react";
import { Anchor } from "../Anchor/Anchor";
import { Checkbox } from "../Checkbox/Checkbox";
import {
	FontIconName,
	Icon,
	IconColor
} from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import styles from "./ListItem.module.less";
import { QuickActions, QuickActionsProps } from "../QuickActions/QuickActions";

export type ListItemProps = {
	isActive?: boolean;
	trackingId?: string;
	interactionMode?: "default" | "none";
	noMobileBorderBottom?: boolean;
	hasStatusDot?: boolean;
	tabIndex?: number;
	onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
	ref?: Ref<HTMLDivElement>;
};

export const ListItem = (props: PropsWithChildren<ListItemProps>): ReactElement => {
	const {
		isActive,
		children,
		trackingId,
		interactionMode = "default",
		noMobileBorderBottom = false,
		hasStatusDot = false,
		tabIndex = -1,
		onKeyDown,
		ref
	} = props;

	const [hasFocusVisible, setHasFocusedVisible] = useState<boolean>(false);

	const contextData = useMemo(
		(): ListItemContextData => ({
			hasFocusVisible
		}),
		[hasFocusVisible]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_active]: isActive,
					[styles.root_noInteraction]: interactionMode === "none",
					[styles.root_hasFocusVisible]: hasFocusVisible
				}
			)}
			tabIndex={tabIndex}
			onFocus={(e) => {
				if (e.currentTarget.matches(":focus-visible")) {
					setHasFocusedVisible(true);
				}
			}}
			onBlur={(e: FocusEvent<HTMLDivElement>): void => {
				if (!e.currentTarget.contains(e.relatedTarget)) {
					setHasFocusedVisible(false);
				}
			}}
			onKeyDown={onKeyDown}
			ref={ref}
		>
			{hasStatusDot && <div className={styles.statusDot}/> }
			<div
				className={classNames(
					styles.host,
					{
						[styles.host_noMobileBorderBottom]: noMobileBorderBottom
					}
				)}
			>
				<ListItemContext.Provider value={contextData}>
					{children}
				</ListItemContext.Provider>
			</div>
		</div>
	);
};
ListItem.displayName = "ListItem";

export type ListItemContentProps = {
	thumbnail?: ReactElement;
	onTouchStart?: TouchEventHandler;
	onTouchMove?: TouchEventHandler;
	onTouchEnd?: TouchEventHandler;
	onContextMenu?: (args: MouseEvent) => void;
	tooltip?: string;
} & ({
	href?: string;
	isTargetBlank?: boolean;
	onClick?: undefined;
} | {
	onClick?: (args: MouseEvent) => void;
	href?: undefined;
});

export const ListItemContent = (props: PropsWithChildren<ListItemContentProps>): ReactElement => {
	const {
		thumbnail,
		onClick,
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		onContextMenu,
		tooltip,
		children
	} = props;

	return (
		<Anchor
			className={styles.content}
			onClick={onClick}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			onContextMenu={onContextMenu}
			tabIndex={-1}
			title={tooltip}
			href={props.href}
			target={
				props.href && props.isTargetBlank
					? "_blank"
					: undefined
			}
		>
			{thumbnail && (
				<div className={styles.thumbnail}>
					{thumbnail}
				</div>
			)}

			<div className={styles.infoContainer}>
				{children}
			</div>
		</Anchor>
	);
};
ListItemContent.displayName = "ListItem.Content";

export type ListItemCheckboxProps = {
	value: boolean;
	onChange: (value: boolean) => void;
};

export const ListItemCheckbox = (props: ListItemCheckboxProps): ReactElement => {
	const {
		value,
		onChange
	} = props;

	const clickHandler = useCallback(
		(args: MouseEvent): void => {
			if (args.currentTarget !== args.target) {
				return;
			}
			onChange(!value);
		},
		[onChange, value]
	);

	return (
		<div
			className={styles.checkbox}
			onClick={clickHandler}
		>
			<Checkbox
				value={value}
				onChange={onChange}
			/>
		</div>
	);
};
ListItemCheckbox.displayName = "ListItem.Checkbox";

export const ListItemCheckboxPlaceholder = (): ReactElement => {

	return (
		<div
			className={classNames(
				styles.checkbox,
				styles.checkbox_placeholder
			)}
		>
			<Checkbox
				value={false}
				onChange={NOOP}
			/>
		</div>
	);
};
ListItemCheckboxPlaceholder.displayName = "ListItem.CheckboxPlaceholder";

function NOOP(): void { /* NOOP */ }

export const ListItemRow = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.row}>
			{children}
		</div>
	);
};
ListItemRow.displayName = "ListItem.Row";

export type ListItemTitleProps = {
	variant?:
		| "default"
		| "link"
		| "secondary";
	tooltip?: string;
	trackingId?: string;
};

export const ListItemTitle = (props: PropsWithChildren<ListItemTitleProps>): ReactElement => {
	const {
		variant = "default",
		tooltip,
		children,
		trackingId
	} = props;

	return (
		<h4
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.title,
				{
					[styles.title_link]: variant === "link",
					[styles.title_secondary]: variant === "secondary"
				}
			)}
			title={tooltip}
		>
			{children}
		</h4>
	);
};
ListItemTitle.displayName = "ListItem.Title";

export type ListItemTitleIconProps = {
	kind: FontIconName;
	color?: IconColor;
	tooltip?: string;
	trackingId?: string;
};

export const ListItemTitleIcon = (props: ListItemTitleIconProps): ReactElement => {
	const {
		kind,
		color,
		tooltip,
		trackingId
	} = props;

	return (
		<span
			{...setTrackingId(trackingId)}
			className={styles.titleIcon}
		>
			<Icon
				value={{
					kind: "font",
					color,
					name: kind
				}}
				tooltip={tooltip}
			/>
		</span>
	);
};
ListItemTitleIcon.displayName = "ListItem.TitleIcon";

export type ListItemSubTitleProps = {
	trackingId?: string;
};

export const ListItemSubTitle = (props: PropsWithChildren<ListItemSubTitleProps>): ReactElement => {
	const {
		children,
		trackingId
	} = props;

	return (
		<span
			{...setTrackingId(trackingId)}
			className={styles.subTitle}
			>
			{children}
		</span>
	);
};
ListItemSubTitle.displayName = "ListItem.SubTitle";

export type ListItemSubTitleIconProps = {
	kind: FontIconName;
};

export const ListItemSubTitleIcon = (props: ListItemSubTitleIconProps): ReactElement => {
	const {
		kind
	} = props;

	return (
		<span className={styles.subTitleIcon}>
			<Icon value={kind} />
		</span>
	);
};
ListItemSubTitleIcon.displayName = "ListItem.SubTitleIcon";

export type ListItemActionProps = {
	icon: FontIconName;
	onClick: () => void;
	bubbleClick?: boolean;
	trackingId: string;
	isActive?: boolean;
};

export const ListItemAction = forwardRef(
	(props: ListItemActionProps, ref: Ref<HTMLButtonElement>): ReactElement => {
		const {
			icon,
			onClick,
			bubbleClick,
			trackingId,
			isActive = false
		} = props;

		return (
			<div
				className={classNames(
					styles.showOnHoverOrTouch,
					styles.actionContainer,
					{ [styles.showOnHoverOrTouch_active]: isActive }
				)}
			>
				<IconButton
					onClick={onClick}
					ref={ref}
					icon={icon}
					bubbleClick={bubbleClick}
					kind="transparent"
					trackingId={trackingId}
					shape="round"
					isActive={isActive}
				/>
			</div>
		);
	}
);
ListItemAction.displayName = "ListItem.Action";

export const ListItemQuickAction = <TActionContext,>(
	props: QuickActionsProps<TActionContext>
): ReactElement => {
	return (
		<div
			className={classNames(
				styles.showOnHoverOrTouch,
				styles.actionContainer,
				{ [styles.showOnHoverOrTouch_active]: props.isMoreActionsOpen }
			)}
		>
			<QuickActions
				{...props}
			/>
		</div>
	);
};
ListItemQuickAction.displayName = "ListItem.ListItemQuick";

ListItem.Content = ListItemContent;
ListItem.Checkbox = ListItemCheckbox;
ListItem.CheckboxPlaceholder = ListItemCheckboxPlaceholder;
ListItem.Action = ListItemAction;
ListItem.Title = ListItemTitle;
ListItem.TitleIcon = ListItemTitleIcon;
ListItem.SubTitle = ListItemSubTitle;
ListItem.SubTitleIcon = ListItemSubTitleIcon;
ListItem.Row = ListItemRow;
ListItem.QuickActions = ListItemQuickAction;

export type ListItemContextData = {
	hasFocusVisible: boolean;
};

export const ListItemContext = createContext<ListItemContextData>({
	hasFocusVisible: false
});

export const useListItemContext = (): ListItemContextData => {
	return useContext(ListItemContext);
};